<template>
  <div class="booking-divider booking-divider-right">
    <div class="booking-content">
      <div v-if="completed" class="d-flex flex-column justify-content-center align-items-center h-100 mt-5 px-4">
        <display-approved v-if="approvedStatus"/>
        <display-under-review v-else-if="bookedStatus"/>
      </div>
      <template v-else>
        <display-header/>
        <template v-if="unknownStatus">
          <display-failed v-if="conflicts" class="mb-2 mx-3">
            <p class="m-0">The time slot you have chosen is no longer available. Please select a different time.</p>
          </display-failed>
          <display-failed v-else-if="pending" class="mb-2 mx-3">
            <p class="m-0">You have a pending booking. Please wait for it to be processed before submitting another.</p>
          </display-failed>
          <display-failed v-else-if="existing" class="mb-2 mx-3">
            <p class="m-0">Please book your appointment through the login page. Click <a class="link link-primary" style="cursor: pointer" @click="gotoLogin">HERE</a>.</p>
          </display-failed>
          <display-failed v-else-if="openSlotUnavailable" class="mb-2 mx-3">
            <p class="m-0">This open slot is no longer available. Click back to redo your booking.</p>
          </display-failed>
        </template>
        <error-banner v-if="errors" :message="errors" class="mb-2 mx-3"/>
        <summary-client v-if="clientTypeId === 2"/>
        <terms-and-conditions v-if="displayTerms"/>
        <display-header-summary v-else/>
        <booking-add v-if="clientTypeId === 1" @displayTerms="handleDisplayTerms" v-model="bookingStatus"/>
        <anonymous-booking-add v-else-if="clientTypeId === 2" @displayTerms="handleDisplayTerms" v-model="bookingStatus"/>
      </template>
    </div>
    <div class="fixed-buttons">
      <button v-if="completed" @click="handleAnotherAppointment" class="btn btn-secondary w-100">
        Book Another Appointment
      </button>
      <template v-else>
        <button class="btn btn-tertiary w-100 mr-2" @click="back" :disabled="loading || pending || existing">Back</button>
        <button @click="triggerBookAppointment" class="btn btn-secondary w-100 ml-2" v-bind:disabled="loading || fetchingClient || pending || existing || openSlotUnavailable">
          Confirm
          <span v-if="loading" class="inline-loader"></span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { ClientUserCheckingMixin } from '@/mixins/Booking/ClientUserCheckingMixin'
import TermsAndConditions from './TermsAndConditions.vue'
import ErrorBanner from '@/components/ErrorValidation/ErrorBanner.vue'
import DisplayHeader from './DisplayHeader.vue'
import DisplayApproved from './DisplayApproved.vue'
import DisplayUnderReview from './DisplayUnderReview.vue'
import DisplayHeaderSummary from './DisplayHeaderSummary.vue'
import DisplayFailed from './DisplayFailed.vue'
import BookingAdd from './Authenticated/BookingAdd.vue'
import AnonymousBookingAdd from './Anonymous/BookingAdd.vue'

export default {
  name: 'Confirmation',
  mixins: [ClientUserCheckingMixin],
  data () {
    return {
      addedBooking: null,
      displayTerms: false,
      bookingStatus: {}
    }
  },
  computed: {
    ...mapState('ApplicationEvents', [
      'bookingAddedEvent'
    ]),
    ...mapState('BookingUpload', [
      'selectedPets'
    ]),
    statuses () {
      return [
        this.bookingStatus
      ]
    },
    completed () {
      return this.statuses.every(s => s.completed)
    },
    loading () {
      return this.statuses.some(s => s.loading)
    },
    errors () {
      const statusErrors = this.statuses.map(s => s.error).filter(error => error)
      if (this.error) {
        statusErrors.push(this.error)
      }
      return statusErrors.join(' ')
    },
    addedBookingStatus () {
      return this.addedBooking?.status ?? null
    },
    unknownStatus () {
      return this.addedBookingStatus?.id === 0
    },
    bookedStatus () {
      return this.addedBookingStatus?.id === 1
    },
    approvedStatus () {
      return this.addedBookingStatus?.id === 2
    },
    conflicts () {
      return this.unknownStatus && this.addedBooking?.conflicts
    },
    pending () {
      return this.unknownStatus && this.addedBooking?.pending
    },
    existing () {
      return this.unknownStatus && this.addedBooking?.existing
    },
    openSlotUnavailable () {
      return this.unknownStatus && this.addedBooking?.openSlotUnavailable
    }
  },
  watch: {
    bookingAddedEvent (booking) {
      this.addedBooking = booking
    },
    conflicts (value) {
      if (value) {
        this.clearAppointmentSearches()
      }
    }
  },
  methods: {
    ...mapMutations('Booking', [
      'clearBooking'
    ]),
    ...mapMutations('ApplicationEvents', [
      'triggerBookAppointment'
    ]),
    ...mapMutations('AppointmentSearches', [
      'clearAppointmentSearches'
    ]),
    back () {
      if (this.completed || this.pending || this.existing) {
        return
      }
      if (this.openSlotUnavailable) {
        this.handleAnotherAppointment()
        return
      }
      this.clearBooking()
      this.$emit('back')
    },
    handleDisplayTerms () {
      this.displayTerms = !this.displayTerms
    },
    handleAnotherAppointment () {
      window.location.reload()
    },
    gotoLogin () {
      this.$router.push({ name: 'login', query: { siteId: this.selectedSiteId } })
    }
  },
  components: {
    'terms-and-conditions': TermsAndConditions,
    'error-banner': ErrorBanner,
    'booking-add': BookingAdd,
    'display-header': DisplayHeader,
    'display-approved': DisplayApproved,
    'display-under-review': DisplayUnderReview,
    'display-header-summary': DisplayHeaderSummary,
    'display-failed': DisplayFailed,
    'anonymous-booking-add': AnonymousBookingAdd
  }
}
</script>

<style scoped>
@media (max-width: 768px) {
  .booking-divider-right {
    height: max-content;
    min-height: 50vh !important;
  }
  .booking-content {
    height: max-content;
    min-height: 40vh !important;
  }
}
</style>
